import React, { useState, useEffect } from "react"
import { motion, useScroll, useTransform, useSpring } from "framer-motion"
import { useNavigate, Link } from "react-router-dom"
import { Heart, Shield, ArrowRight, Gamepad2, Calendar, MessageCircle } from 'lucide-react'
import { Button } from "./ui/Button"
import GameParticleCanvas from "./GameParticleCanvas"
import styles from "./splash.module.css"
import { ChatAnimatedScene } from "./ChatAnimatedScene"
import { GamesAnimatedScene } from "./GamesAnimatedScene"
import { DateAnimatedScene } from "./DateAnimatedScene"
import { useSEO } from "../../hooks"

function SplashScreen() {
  const navigate = useNavigate()
  const [isVisible, setIsVisible] = useState(false)

  useSEO({
    title: "Plenty of Hearts — Find Your Perfect Match by 2030",
    description:
      "Play games, go on dates, and chat with people who share your interests. Plenty of Hearts is a dating app built around gamified icebreakers and real connection.",
    canonical: "/",
  })

  const [floatingHearts, setFloatingHearts] = useState<Array<{
    left: string
    top: string
    animationDelay: string
    animationDuration: string
    width: string
    height: string
  }>>([])

  // Framer Motion Scroll Setup
  const { scrollY: rawScrollY } = useScroll()
  const smoothScrollY = useSpring(rawScrollY, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  })

  // Parallax transform for the hero content
  const heroTranslateY = useTransform(smoothScrollY, [0, 1000], [0, 500])
  const heroOpacity = useTransform(smoothScrollY, [0, 500], [1, 0])

  useEffect(() => {
    setIsVisible(true)
  }, [])

  // Generate floating hearts data client-side only to avoid hydration mismatch
  useEffect(() => {
    const hearts = [...Array(8)].map(() => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${10 + Math.random() * 5}s`,
      width: `${40 + Math.random() * 60}px`,
      height: `${40 + Math.random() * 60}px`,
    }))
    setFloatingHearts(hearts)
  }, [])

  return (
    <div className={`${styles.splashContainer} min-h-screen bg-white`}>
      <section className="relative h-screen overflow-hidden">
        {/* Video Background Layer */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/POH%20Video-q5vxgZuUtqD4sD6tSyY24eDWE6foaR.mp4" type="video/mp4" />
          </video>

          {/* Gradient overlays for depth and readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-accent/20" />
        </div>


        {/* Floating geometric shapes for depth */}
        <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
          {floatingHearts.map((heart, i) => (
            <div
              key={i}
              className="absolute animate-float-diagonal opacity-10"
              style={{
                left: heart.left,
                top: heart.top,
                animationDelay: heart.animationDelay,
                animationDuration: heart.animationDuration,
              }}
            >
              <Heart
                className="fill-primary text-primary drop-shadow-[0_0_20px_rgba(236,100,53,0.8)]"
                style={{
                  width: heart.width,
                  height: heart.height,
                }}
              />
            </div>
          ))}
        </div>

        {/* Main Content Layer with Liquid Parallax Effect */}
        <motion.div
          className="relative z-20 h-full flex flex-col items-center justify-center px-6"
          style={{
            y: heroTranslateY,
            opacity: heroOpacity,
          }}
        >
          <div className="max-w-5xl mx-auto text-center space-y-8">
            {/* Animated Headline with futuristic styling */}
            <div
              className={`transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                }`}
            >
              <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold leading-tight mb-6 text-white">
                <span
                  className="inline-block"
                  style={{
                    color: '#ffffff',
                    textShadow: '0 0 40px rgba(236, 100, 53, 1), 0 0 80px rgba(236, 100, 53, 0.8), 0 0 120px rgba(236, 100, 53, 0.6)',
                    backgroundImage: 'linear-gradient(90deg, #ffffff 0%, #ec6435 50%, #ff9f40 100%)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: 'gradient-x 3s ease infinite'
                  }}
                >
                  Find Your
                </span>
                <br />
                <span
                  className="inline-block"
                  style={{
                    color: '#ffffff',
                    textShadow: '0 0 40px rgba(255, 159, 64, 1), 0 0 80px rgba(255, 159, 64, 0.8), 0 0 120px rgba(255, 159, 64, 0.6)',
                    backgroundImage: 'linear-gradient(90deg, #ff9f40 0%, #ffffff 50%, #ec6435 100%)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: 'gradient-x 3s ease infinite',
                    animationDelay: '0.5s'
                  }}
                >
                  Perfect Match
                </span>
                <br />
                <span
                  className="inline-block"
                  style={{
                    color: '#ffffff',
                    textShadow: '0 0 40px rgba(236, 100, 53, 1), 0 0 80px rgba(236, 100, 53, 0.8), 0 0 120px rgba(236, 100, 53, 0.6)',
                    backgroundImage: 'linear-gradient(90deg, #ec6435 0%, #ff9f40 50%, #ffffff 100%)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: 'gradient-x 3s ease infinite',
                    animationDelay: '1s'
                  }}
                >
                  By 2030
                </span>
              </h1>
            </div>

            {/* Subtitle with glow effect */}
            <div
              className={`text-xl md:text-3xl text-white/90 font-medium max-w-3xl mx-auto leading-relaxed drop-shadow-[0_0_15px_rgba(0,0,0,0.8)] transition-all duration-1000 delay-300 space-y-2 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                }`}
            >
              <p>Play games, go on dates, and chat with people who share your interests.</p>
            </div>

            {/* CTA Button with futuristic styling */}
            <div
              className={`transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                }`}
            >
              <Button
                size="lg"
                className="relative !bg-[#EC6435] hover:!bg-[#FF8C42] !text-white rounded-full px-8 py-5 text-base sm:px-12 sm:py-6 sm:text-xl md:px-16 md:py-8 md:text-2xl font-bold shadow-[0_0_40px_rgba(236,100,53,0.6)] hover:shadow-[0_0_60px_rgba(236,100,53,0.9)] hover:scale-110 transition-all duration-500 overflow-hidden border-2 border-orange-400/50 group max-w-full"
                style={{ backgroundColor: '#EC6435', color: '#ffffff' }}
                onClick={() => navigate('/login')}
              >
                {/* Animated shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

                {/* Button content */}
                <span className="relative z-10 flex items-center gap-2 sm:gap-3 !text-white">
                  Login/Register
                  <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 group-hover:translate-x-2 transition-transform duration-300 !text-white" />
                </span>

                {/* Pulsing glow rings */}
                <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute inset-0 rounded-full border-2 border-orange-300/70 animate-ping" />
                </div>
              </Button>
            </div>

            {/* Trust Indicators with enhanced styling */}
            <div
              className={`flex flex-wrap items-center justify-center gap-8 md:gap-12 mt-12 transition-all duration-1000 delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                }`}
            >
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 hover:bg-white/20 hover:scale-110 transition-all duration-300">
                  <div className="relative">
                    <div className="absolute inset-0 bg-accent/50 blur-lg rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
                    <Shield className="relative w-6 h-6 text-white drop-shadow-[0_0_10px_rgba(255,159,64,0.8)]" />
                  </div>
                  <span className="text-white font-semibold text-lg">100% Secure</span>
                </div>
                <p className="text-[10px] text-white/40 italic font-medium tracking-tight">
                  Disclaimer: Not Guaranteed To Find The Perfect Match By 2030
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Games Section */}
      <section id="games" className="px-4 sm:px-6 py-16 sm:py-24 bg-gray-50 relative overflow-hidden">
        {/* Background GIF layer */}
        <div className="absolute inset-0 opacity-40">
          <img
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/I_need_you_202510261709.gif-O0m47ocjBiQ51XCIS8t9RODi3EDEJZ.jpeg"
            alt=""
            className="w-full h-full object-cover scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 via-muted/30 to-muted/50" />
        </div>

        {/* Canvas particle background */}
        <div className="absolute inset-0 opacity-10">
          <GameParticleCanvas />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary/10 border border-primary/30 backdrop-blur-sm">
                <Gamepad2 className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                <span className="text-sm sm:text-base text-primary font-semibold">Games</span>
              </div>

              {/* Heading */}
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
                <span className="text-gray-900">Break </span>
                <span className="text-primary">the ice</span>
                <span className="text-accent"> with fun</span>
                <br />
                <span className="text-gray-900">games</span>
              </h2>

              {/* Description */}
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
                Level up your connections through gamified interactions. Play cards, unlock achievements, and earn insights that reveal authentic compatibility. Every game you play builds your profile and helps AI understand what makes you click.
              </p>
            </div>

            {/* Interactive game preview showcase */}
            <div className="relative h-[350px] sm:h-[400px] md:h-[500px] rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10 border border-primary/20 backdrop-blur-sm shadow-[0_0_50px_rgba(236,100,53,0.3)]">
              <GamesAnimatedScene />
            </div>
          </div>
        </div>
      </section>

      {/* Date Section */}
      <section id="date" className="px-4 sm:px-6 py-16 sm:py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-accent/10 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div
              className="relative h-[350px] sm:h-[400px] md:h-[500px] rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-br from-secondary/10 via-primary/10 to-accent/10 border border-secondary/20 backdrop-blur-sm shadow-[0_0_50px_rgba(255,99,132,0.3)] order-2 md:order-1"
            >
              <DateAnimatedScene />
            </div>

            <div className="space-y-6 sm:space-y-8 order-1 md:order-2">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-secondary/10 border border-secondary/30 backdrop-blur-sm">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-secondary" />
                <span className="text-sm sm:text-base text-secondary font-semibold">Date</span>
              </div>

              {/* Heading */}
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
                <span className="text-gray-900">Plan </span>
                <span className="text-secondary">meaningful</span>
                <span className="text-accent"> dates</span>
              </h2>

              {/* Description */}
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
                Get personalized date ideas powered by AI that learns from your conversations and shared interests. Our system suggests activities and locations that align with what you've discovered about each other through games and chats, making planning feel natural and authentic.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Chat Section */}
      <section id="chat" className="px-4 sm:px-6 py-16 sm:py-24 bg-gray-50 relative overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-accent/10 border border-accent/30 backdrop-blur-sm">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                <span className="text-sm sm:text-base text-accent font-semibold">Chat</span>
              </div>

              {/* Heading */}
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
                <span className="text-gray-900">Connect </span>
                <span className="text-accent">through</span>
                <br />
                <span className="text-primary">conversation</span>
              </h2>

              {/* Description */}
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
                Chat with AI-assisted conversation starters and smart icebreakers that keep the dialogue flowing. Get real-time suggestions based on your match's interests and your conversation history, helping you connect more authentically.
              </p>
            </div>

            <div className="relative h-[350px] sm:h-[400px] md:h-[500px] rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-br from-accent/10 via-primary/10 to-secondary/10 border border-accent/20 backdrop-blur-sm shadow-[0_0_50px_rgba(255,159,64,0.3)]">
              <ChatAnimatedScene />
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 py-16 sm:py-24 md:py-32 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        {/* Decorative lighting effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 sm:w-80 md:w-96 h-64 sm:h-80 md:h-96 bg-primary/20 rounded-full blur-[80px] sm:blur-[100px] md:blur-[120px] animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-64 sm:w-80 md:w-96 h-64 sm:h-80 md:h-96 bg-accent/20 rounded-full blur-[80px] sm:blur-[100px] md:blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-4 sm:space-y-6 md:space-y-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight">
            Ready to play your cards?
          </h2>

          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-600 font-medium">
            Join the next generation of dating.
          </p>

          <div className="pt-4 sm:pt-6">
            <Button
              size="lg"
              className="relative overflow-hidden group rounded-full px-7 py-4 text-base sm:px-10 sm:py-5 sm:text-lg md:px-12 md:py-7 md:text-xl font-bold transition-all duration-500 hover:scale-110 active:scale-95 border-2 border-white/20 max-w-full"
              onClick={() => navigate('/login')}
            >
              {/* Animated Background Layer */}
              <div className="absolute inset-0 bg-primary bg-[length:200%_auto] animate-gradient-x"
                style={{ backgroundImage: 'linear-gradient(90deg, #ec6435 0%, #ff9f40 50%, #ec6435 100%)' }} />

              {/* Shimmer Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

              {/* Content */}
              <span className="relative z-10 flex items-center gap-3 text-white">
                Login / Register
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
              </span>

              {/* Pulsing Glow */}
              <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[0_0_40px_rgba(236,100,53,0.8)]" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex flex-col items-center space-y-6">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: 'rgba(236, 100, 53, 0.9)' }}>
                <Heart className="w-5 h-5 text-white fill-white stroke-white" style={{ color: '#ffffff', fill: '#ffffff', stroke: '#ffffff' }} />
              </div>
              <span className="text-xl font-bold text-gray-900">Plenty of Hearts</span>
            </div>

            {/* Copyright */}
            <p className="text-gray-500 text-center">
              © 2026 Plenty of Hearts. Connecting hearts worldwide.
            </p>

            {/* Footer links */}
            <div className="flex flex-wrap items-center justify-center gap-6">
              <Link to="/privacy-policy" className="text-gray-500 hover:text-primary transition-colors duration-300">
                Privacy
              </Link>
              <Link to="/terms-of-service" className="text-gray-500 hover:text-primary transition-colors duration-300">
                Terms
              </Link>
              <Link to="/support" className="text-gray-500 hover:text-primary transition-colors duration-300">
                Support
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default SplashScreen
export { SplashScreen }
